import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValid } from "date-fns";
import {
  CreateOrUpdateTransactionParams,
  TransactionTypeEnum,
} from "@rentablo/api/apiv1";
import Calendar from "@icons/myicons/calendar.svg?react";
import { parseOrUndefined, toCurrency, toString } from "@helpers";
import { isStringDateValid } from "@helpers/date/validation";
import { LoadingButton } from "@rentablo/components";
import { CurrencyMode, CurrencyModeRadioGroup } from "@components";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { validateTransactionValues } from "../utils";
import { AddManuallyWarnings } from "./AddManuallyWarnings";
import { AddManuallyMessage } from "./AddManuallyMessage";
import { AddManuallyAdditionalInformation } from "./AddManuallyAdditionalInformation";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
  max-width: 700px;
`;

const SubmitButton = styled(LoadingButton)`
  align-self: flex-start;
`;

const transactionTypes: TransactionTypeEnum[] = [
  "withdrawal",
  "deposit",
  "interest",
  "tax_refund",
  "other_gains",
];

const taxAvailableTypes = ["interest", "other_gains"];

interface AddManuallyPageFormProps {
  isAccountsLoading: boolean;
  isSavingLoading: boolean;
  errorMessage?: string;
  requestStatus?: "success" | "error";
  accountCurrency?: string;
  onSubmit: (params: CreateOrUpdateTransactionParams) => void;
  onReset: () => void;
}

export const AddManuallyPageForm = ({
  isAccountsLoading,
  isSavingLoading,
  errorMessage,
  requestStatus,
  accountCurrency,
  onSubmit,
  onReset,
}: AddManuallyPageFormProps) => {
  const { t } = useTranslation();

  const [values, setValues] = useState<CreateOrUpdateTransactionParams>({
    type: "withdrawal",
    amount: 0,
    taxAmount: 0,
    valueDate: new Date().toISOString(),
    purpose: "",
  });
  const [amount, setAmount] = useState("0");
  const [taxAmount, setTaxAmount] = useState("0");
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>("account");
  const { globalCurrency } = useCurrencyDisplay();
  const inputCurrency =
    currencyMode === "global"
      ? globalCurrency
      : (accountCurrency ?? globalCurrency);

  const handleCommentChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.currentTarget;

    setValues({
      ...values,
      purpose: value,
    });
    onReset();
  };

  const handleAmountChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.currentTarget;
    const parsedAmount = parseOrUndefined(value);

    setAmount(toString(parsedAmount, value, 2));
    setValues({
      ...values,
      amount: parsedAmount,
    });
    onReset();
  };

  const handleTaxAmountChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.currentTarget;
    const parsedTaxAmount = parseOrUndefined(value);

    setTaxAmount(toString(parsedTaxAmount, value, 2));
    setValues({
      ...values,
      taxAmount: parsedTaxAmount,
    });
    onReset();
  };

  const handleSelectChange = (e: SelectChangeEvent<TransactionTypeEnum>) => {
    const { value } = e.target;
    const newValue = {
      ...values,
      type: e.target.value as CreateOrUpdateTransactionParams["type"],
    };

    if (value !== "interest" && value !== "other_gains") {
      newValue.taxAmount = 0;
    }

    setValues(newValue);
    onReset();
  };

  const handleDate = (value: Date | null) => {
    if (value && isValid(value)) {
      setValues({ ...values, valueDate: value.toISOString() });
      return;
    }

    setValues({ ...values, valueDate: undefined });
    onReset();
  };

  const handleSubmit = () => {
    onSubmit({ ...values, currency: inputCurrency });
  };

  const showTaxAmount = taxAvailableTypes.find((item) => item === values.type);
  const totalAmount = (values?.taxAmount ?? 0) + (values?.amount ?? 0);
  const canSubmit =
    isStringDateValid(values?.valueDate) &&
    !isAccountsLoading &&
    !isSavingLoading &&
    validateTransactionValues(values);

  return (
    <Container>
      <AddManuallyMessage type={requestStatus} message={errorMessage} />
      <FormControl>
        <InputLabel>{t("addManuallyPage.form.type")}</InputLabel>
        <Select
          data-testid="transaction-type-field"
          displayEmpty
          value={values.type}
          inputProps={{
            "data-testid": "transaction-type-select",
          }}
          onChange={handleSelectChange}
        >
          {transactionTypes.map((item) => (
            <MenuItem
              key={item}
              value={item}
              data-testid={`type-select-option-${item}`}
            >
              {t(`addManuallyPage.form.types.${item}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <AddManuallyAdditionalInformation />
      <AddManuallyWarnings transactionType={values.type} />
      <TextField
        data-testid="amount-field"
        value={amount}
        name="amount"
        label={t("addManuallyPage.form.netAmount")}
        onChange={handleAmountChange}
        slotProps={{
          htmlInput: {
            "data-testid": "amount-input",
          },
        }}
      />
      <CurrencyModeRadioGroup
        accountCurrency={accountCurrency}
        value={currencyMode}
        onChange={setCurrencyMode}
      />
      {Boolean(showTaxAmount) && (
        <>
          <TextField
            data-testid="tax-amount-field"
            value={taxAmount}
            name="taxAmount"
            onChange={handleTaxAmountChange}
            label={t("addManuallyPage.form.taxAmount")}
            slotProps={{
              htmlInput: {
                "data-testid": "tax-amount-input",
              },
            }}
          />
          <Typography>
            {t("addManuallyPage.form.totalAmount")}{" "}
            {toCurrency(totalAmount, inputCurrency)}
          </Typography>
        </>
      )}
      <DatePicker
        data-testid="value-date-picker"
        disableFuture
        openTo="year"
        format="dd.MM.yyyy"
        label={t("addManuallyPage.form.date")}
        views={["year", "month", "day"]}
        value={new Date(values.valueDate ?? "")}
        slotProps={{
          textField: {
            inputProps: {
              "data-testid": "value-date-picker-text-field",
            },
          },
        }}
        onChange={handleDate}
        slots={{
          openPickerIcon: () => <Calendar width="20px" height="20px" />,
        }}
      />
      <TextField
        name="purpose"
        label={t("addManuallyPage.form.purpose")}
        rows={4}
        multiline
        value={values.comment}
        onChange={(event) => handleCommentChange(event)}
      />
      <SubmitButton
        data-testid="submit-button"
        variant="primary"
        disabled={!canSubmit}
        isFetching={isSavingLoading}
        onClick={handleSubmit}
      >
        {t("addManuallyPage.form.submit")}
      </SubmitButton>
    </Container>
  );
};
